// @catalog F.030
// TabPorTienda — Ventas por tienda últimos 4 meses con monto y qty en la
// misma celda (CSS grid en lugar de <Table>, según AGENT_PROMPT).

import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { fmtCurrencyK, fmtNum, formatMonthLabel } from "./helpers";

export interface StoreSalesRow {
  locationName: string;
  months: Record<string, number>;
  amounts: Record<string, number>;
  total: number;
  totalAmount: number;
}

export interface StoreSalesData {
  stores: StoreSalesRow[];
  monthKeys: string[];
  grandTotal: number;
  grandTotalAmount: number;
}

interface TabPorTiendaProps {
  storeSales: StoreSalesData | null;
  loading: boolean;
  errorMessage: string | null;
}

const gridTemplate = "1fr repeat(4, minmax(72px, 1fr)) minmax(90px, auto)";

function Cell({
  amount,
  qty,
  zero,
  bold,
  align = "right",
}: {
  amount?: string;
  qty?: number;
  zero?: boolean;
  bold?: boolean;
  align?: "left" | "right";
}) {
  return (
    <Box
      sx={{
        textAlign: align,
        fontVariantNumeric: "tabular-nums",
        fontSize: 12.5,
        color: zero ? "var(--ink-4)" : "var(--ink-1)",
        fontWeight: bold ? 600 : 400,
      }}
    >
      {amount ?? "—"}
      {qty != null && qty > 0 && (
        <Box
          component="span"
          sx={{
            display: "block",
            fontSize: 11,
            color: "var(--ink-3)",
            fontWeight: bold ? 600 : 400,
          }}
        >
          {fmtNum(qty)} pz
        </Box>
      )}
    </Box>
  );
}

export function TabPorTienda({
  storeSales,
  loading,
  errorMessage,
}: TabPorTiendaProps) {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="warning" variant="outlined">
        No se pudo cargar la fuente canónica de ventas por tienda:{" "}
        {errorMessage}
      </Alert>
    );
  }

  if (!storeSales || storeSales.stores.length === 0) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          color: "var(--ink-3)",
          fontSize: 13,
        }}
      >
        Sin ventas registradas en los últimos 4 meses.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 1.25,
        p: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          fontWeight: 500,
          letterSpacing: "0.02em",
          mb: 1.25,
        }}
      >
        Ventas por tienda · últimos 4 meses
      </Typography>

      {/* Header row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap: "4px",
          alignItems: "center",
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--ink-3)",
          fontWeight: 500,
          borderBottom: "1px solid var(--border-strong)",
          pb: 0.75,
        }}
      >
        <Box sx={{ textAlign: "left" }}>Tienda</Box>
        {storeSales.monthKeys.map((mk) => (
          <Box key={mk} sx={{ textAlign: "right" }}>
            {formatMonthLabel(mk)}
          </Box>
        ))}
        <Box sx={{ textAlign: "right" }}>Total</Box>
      </Box>

      {/* Data rows */}
      {storeSales.stores.map((store) => (
        <Box
          key={store.locationName}
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            gap: "4px",
            alignItems: "center",
            py: 1,
            borderBottom: "1px solid var(--border)",
            fontSize: 12.5,
          }}
        >
          <Box
            sx={{
              fontWeight: 500,
              color: "var(--ink-1)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {store.locationName}
          </Box>
          {storeSales.monthKeys.map((mk) => {
            const amt = store.amounts?.[mk] ?? 0;
            const qty = store.months[mk] ?? 0;
            return (
              <Cell
                key={mk}
                amount={amt > 0 ? fmtCurrencyK(amt) : "—"}
                qty={qty}
                zero={amt === 0}
              />
            );
          })}
          <Cell amount={fmtCurrencyK(store.totalAmount)} qty={store.total} bold />
        </Box>
      ))}

      {/* Total row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridTemplate,
          gap: "4px",
          alignItems: "center",
          py: 1,
          borderTop: "2px solid var(--border-strong)",
          mt: 0.5,
          fontWeight: 600,
        }}
      >
        <Box sx={{ fontWeight: 600, color: "var(--ink-1)", fontSize: 12.5 }}>
          Total
        </Box>
        {storeSales.monthKeys.map((mk) => {
          const amt = storeSales.stores.reduce(
            (sum, s) => sum + (s.amounts?.[mk] ?? 0),
            0,
          );
          const qty = storeSales.stores.reduce(
            (sum, s) => sum + (s.months[mk] ?? 0),
            0,
          );
          return <Cell key={mk} amount={fmtCurrencyK(amt)} qty={qty} bold />;
        })}
        <Cell
          amount={fmtCurrencyK(storeSales.grandTotalAmount)}
          qty={storeSales.grandTotal}
          bold
        />
      </Box>
    </Box>
  );
}

export default TabPorTienda;
